/**
 * Client-side image compression utility
 * 
 * Compresses and resizes images before upload to reduce file size while maintaining quality.
 * This improves upload speed and reduces bandwidth usage.
 */

const MAX_DIMENSION = 2000 // Match backend ImagePreprocessingService.MAX_DIMENSION
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const JPEG_QUALITY_START = 0.92 // Start with high quality
const JPEG_QUALITY_MIN = 0.70 // Minimum quality threshold

interface CompressionOptions {
  maxWidth?: number
  maxHeight?: number
  maxFileSize?: number
  quality?: number
}

/**
 * Compress and resize an image file
 * 
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns A Promise that resolves to a compressed File object
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxWidth = MAX_DIMENSION,
    maxHeight = MAX_DIMENSION,
    maxFileSize = MAX_FILE_SIZE,
    quality = JPEG_QUALITY_START,
  } = options

  // Check for HEIC/HEIF files first (Canvas API cannot decode these)
  const normalizedType = (file.type || '').toLowerCase()
  const fileName = file.name.toLowerCase()
  const isHeic = normalizedType.includes('heic') || 
                 normalizedType.includes('heif') ||
                 fileName.endsWith('.heic') ||
                 fileName.endsWith('.heif')
  
  if (isHeic) {
    throw new Error('HEIC format cannot be compressed in the browser. Please convert to JPEG first.')
  }

  // If file is already small enough, return as-is
  if (file.size <= maxFileSize) {
    return file
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img
          
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = Math.round((height * maxWidth) / width)
              width = maxWidth
            } else {
              width = Math.round((width * maxHeight) / height)
              height = maxHeight
            }
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }

          // Use high-quality image rendering
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          
          // Draw the resized image
          ctx.drawImage(img, 0, 0, width, height)

          // Try to compress with decreasing quality until we meet size requirements
          const tryCompress = (currentQuality: number): void => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to compress image'))
                  return
                }

                // If blob is small enough or we've hit minimum quality, use it
                if (blob.size <= maxFileSize || currentQuality <= JPEG_QUALITY_MIN) {
                  // Create a new File object with the same name and type
                  // Convert PNG to JPG filename for better compression
                  let fileName = file.name
                  if (fileName.toLowerCase().endsWith('.png')) {
                    fileName = fileName.slice(0, -4) + '.jpg'
                  } else if (!fileName.toLowerCase().endsWith('.jpg') && !fileName.toLowerCase().endsWith('.jpeg')) {
                    fileName = fileName.split('.')[0] + '.jpg'
                  }
                  
                  const compressedFile = new File(
                    [blob],
                    fileName,
                    { type: 'image/jpeg', lastModified: Date.now() }
                  )
                  resolve(compressedFile)
                } else {
                  // Try with lower quality
                  const newQuality = Math.max(currentQuality - 0.1, JPEG_QUALITY_MIN)
                  tryCompress(newQuality)
                }
              },
              'image/jpeg', // Always convert to JPEG for better compression
              currentQuality
            )
          }

          // Start compression
          tryCompress(quality)
        } catch (error) {
          reject(error instanceof Error ? error : new Error('Image compression failed'))
        }
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      if (e.target?.result) {
        img.src = e.target.result as string
      } else {
        reject(new Error('Failed to read file'))
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Check if an image file needs compression
 */
export function needsCompression(file: File, maxFileSize: number = MAX_FILE_SIZE): boolean {
  return file.size > maxFileSize
}
